import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip, CircularProgress, TextField, Button, Divider, Typography, Card, CardContent, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AddCircle, ChevronLeft, ChevronRight, Delete, CloudUpload } from "@mui/icons-material";
import Datagrid from "../Datagrid/Datagrid";
import { LABELS, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { createProduct, deleteProducts, getProducts, updateProduct, uploadProductAssets } from "../../services/manageProductsMasterService";
import Swal from "sweetalert2";

const ManageProductsMaster = () => {
	const normalizeDateValue = (value) => {
		if (!value) return "1900-01-01";
		const raw = String(value);
		if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
		const parsed = new Date(raw);
		if (Number.isNaN(parsed.getTime())) return "1900-01-01";
		return parsed.toISOString().slice(0, 10);
	};
	const [allProducts, setAllProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [selection, setSelection] = useState([]);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editRow, setEditRow] = useState(null);
	const [form, setForm] = useState({
		product_number: "",
		product_name: "",
		product_description: "",
		product_sell_end_date: "",
		product_rspl_code: "",
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formAlert, setFormAlert] = useState(null);
	const [files, setFiles] = useState({ image: null });
	const {t} = useTranslation();

	useEffect(() => {
		const fetchAllProducts = async () => {
			try {
				const response = await getProducts();
				setAllProducts(response.data);
			} catch (error) {
				setError(error.message);
			} finally {
				setLoading(false);
			}
		};
		fetchAllProducts();
	}, []);

	const handleEdit = (product) => {
		setEditRow(product);
		setForm({
			product_number: product.product_number || "",
			product_name: product.product_name || "",
			product_description: product.product_description || "",
			product_sell_end_date: normalizeDateValue(product.product_sell_end_date),
			product_rspl_code: product.product_rspl_code || "",
		});
		setFiles({ image: null });
		setFormAlert(null);
		setDialogOpen(true);
	};

	const handleAdd = () => {
		setEditRow(null);
		setForm({
			product_number: "",
			product_name: "",
			product_description: "",
			product_sell_end_date: "",
			product_rspl_code: "",
		});
		setFiles({ image: null });
		setFormAlert(null);
		setDialogOpen(true);
	};

	const refresh = async () => {
		const response = await getProducts();
		setAllProducts(response.data);
	};

	const uploadAssets = async (productNumber) => {
		if (!files.image) return;
		const fd = new FormData();
		fd.append("product_number", productNumber);
		fd.append("image", files.image);
		await uploadProductAssets(fd);
	};

	const handleSave = async () => {
		try {
			if (!form.product_number || !form.product_name) {
				setFormAlert({ severity: "warning", message: "Product number and name are required." });
				Swal.fire({
					icon: "warning",
					title: t('ALERT_MESSAGES.WARNING'),
					text: "Product number and name are required.",
				});
				return;
			}
			const nextNumber = `${form.product_number || ""}`.trim().toLowerCase();
			const hasDuplicate = allProducts.some((product) => {
				if (!product?.product_number) return false;
				if (editRow && product.product_id === editRow.product_id) return false;
				return String(product.product_number).trim().toLowerCase() === nextNumber;
			});
			if (hasDuplicate) {
				setFormAlert({ severity: "error", message: "Product number already exists." });
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: "Product number already exists.",
				});
				return;
			}
			setIsSubmitting(true);
			const payload = {
				product_number: form.product_number,
				product_name: form.product_name,
				product_description: form.product_description,
				product_sell_end_date: form.product_sell_end_date,
				product_rspl_code: form.product_rspl_code,
				product_search_name: editRow?.product_search_name || form.product_name || "",
				product_bom_level: editRow?.product_bom_level || "",
				product_buyer_group_id: editRow?.product_buyer_group_id || "",
				product_sell_price: editRow?.product_sell_price ?? "",
				product_data_area_id: editRow?.product_data_area_id || "",
			};
			if (editRow) {
				await updateProduct(editRow.product_id, payload);
			} else {
				await createProduct(payload);
			}
			await uploadAssets(form.product_number);
			await refresh();
			setDialogOpen(false);
			Swal.fire({
				icon: "success",
				title: t('ALERT_MESSAGES.SUCCESS'),
				text: editRow ? t('ALERT_MESSAGES.PRODUCT_UPDATED_SUCCESSFULLY') : t('ALERT_MESSAGES.PRODUCT_CREATED_SUCCESSFULLY'),
			});
		} catch (e) {
			console.error(e);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: "Failed to save product.",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (selection.length === 0) return;
		try {
			const confirmation = await Swal.fire({
				title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
				text: "This will delete the product(s) permanently.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText:  t('GENERAL.YES_DELETE_IT'),
			});
			if (!confirmation.isConfirmed) return;
			await deleteProducts(selection);
			await refresh();
			setSelection([]);
			Swal.fire({
				icon: "success",
				title: t('ALERT_MESSAGES.SUCCESS'),
				text: "Product(s) deleted successfully.",
			});
		} catch (e) {
			console.error(e);
		}
	};

	const columns = [
		{ field: "product_number", headerName: t('TABLE_HEADERS.PRODUCT_NUMBER'), width: 200 },
		{ field: "product_name", headerName: t('TABLE_HEADERS.PRODUCT_NAME'), flex: 1 },
		{ field: "product_rspl_code", headerName: t('BUTTON.RSPL_CODE'), width: 140 },
		{ field: "product_sell_end_date", headerName: t('GENERAL.SELL_END_DATE'), width: 160 },
		{ field: "product_data_area_id", headerName: t('TABLE_HEADERS.DATA_AREA_ID'), width: 140 },
		{ field: "product_sell_price", headerName: t('GENERAL.SELL_PRICE'), width: 120 },
		{
			headerName: t('TABLE_HEADERS.ACTIONS'),
			sortable: false,
			width: 100, // Ensure there's enough space for icons
			headerAlign: "center",
			align: "left",
			renderCell: (params) => (
				<div>
					<Tooltip title={t('UI_TEXT.EDIT_USER')}>
						<IconButton
							color="primary"
							aria-label="Edit User"
							onClick={(event) => {
								event.stopPropagation();
								handleEdit(params.row);
							}}>
							<EditIcon />
						</IconButton>
					</Tooltip>
				</div>
			),
		},
	];

	const customButtons = [
		{
			label: "Add",
			icon: <AddCircle />,
			onClick: handleAdd,
		},
		{
			label: t('LABELS.DELETE_SELECTED'),
			icon: <Delete />,
			onClick: handleDelete,
		},
	];

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<div>
			<Datagrid
				loading={loading}
				data={allProducts}
				columns={columns}
				customButtons={customButtons}
				checkboxSelection={true}
				rowIdField="product_id"
				onSelect={setSelection}
				sortBy={[{ field: "product_id", sort: "asc" }]}
				hideFilterFromColumnMenu
				lockActiveColumnInManageColumns
				preventHidingLastColumn
			/>
			<ModalWrapper
				open={dialogOpen}
				setOpen={setDialogOpen}
				title={editRow ? t('BUTTON.EDIT_PRODUCT') : t('BUTTON.ADD_PRODUCT')}
				content={
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
						{formAlert && (
							<Alert severity={formAlert.severity || "error"}>{formAlert.message}</Alert>
						)}
						<Box>
							<Typography variant="subtitle1" fontWeight={600}>{t('UI_TEXT.PRODUCT_DETAILS')}</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('UI_TEXT.PRODUCT_DETAILS_DESCRIPTION')}
							</Typography>
						</Box>
						<TextField label={t('TABLE_HEADERS.PRODUCT_NUMBER')} value={form.product_number} onChange={(e) => setForm({ ...form, product_number: e.target.value })} size="small" />
						<TextField label={t('TABLE_HEADERS.PRODUCT_NAME')} value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} size="small" />
						<TextField label={t('TABLE_HEADERS.DESCRIPTION')} value={form.product_description} onChange={(e) => setForm({ ...form, product_description: e.target.value })} multiline minRows={3} size="small" />
						<TextField
							label={t('GENERAL.SELL_END_DATE')}
							value={form.product_sell_end_date}
							onChange={(e) => setForm({ ...form, product_sell_end_date: e.target.value })}
							type="date"
							size="small"
							InputLabelProps={{ shrink: true }}
							inputProps={{ placeholder: "" }}
						/>
						<Divider />
						<Box>
							<Typography variant="subtitle1" fontWeight={600}>{t('GENERAL.RSPL_DETAILS_TITLE')}</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('GENERAL.RSPL_DETAILS_DESCRIPTION')}
							</Typography>
						</Box>
						<TextField label={t('BUTTON.RSPL_CODE')} value={form.product_rspl_code} onChange={(e) => setForm({ ...form, product_rspl_code: e.target.value })} size="small" />
						<Divider />
						<Box>
							<Typography variant="subtitle1" fontWeight={600}>{t('UI_TEXT.PRODUCT_IMAGE_TITLE')}</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('UI_TEXT.PRODUCT_IMAGE_DESCRIPTION')}
							</Typography>
						</Box>
						<Box
							sx={{
								borderRadius: "10px",
								background: "#eee",
								px: 1.5,
								py: 1,
								display: "flex",
								justifyContent: "flex-start",
								alignItems: "center",
								width: "100%",
								columnGap: 2,
							}}
						>
							<Button
								variant="contained"
								color="primary"
								size="small"
								startIcon={<CloudUpload />}
								disabled={Boolean(files.image)}
								onClick={() => document.getElementById("productImageInput").click()}
							>
							{t('FILE_UPLOAD.CHOOSE_FILE')}
							</Button>
							<Typography variant="body2" fontSize="11px">
								{files.image ? files.image?.name : t('UI_TEXT.ATTACH_PRODUCT_IMAGE_PNG')}
							</Typography>
						</Box>
						<input
							type="file"
							id="productImageInput"
							accept=".png"
							style={{ display: "none" }}
							onChange={e => setFiles({ image: e.target.files?.[0] || null })}
						/>
						{files.image && (
							<Card
								variant="outlined"
								sx={{
									width: "100%",
									"&:hover": { backgroundColor: "action.hover" },
								}}
							>
								<CardContent
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										py: 1.5,
										"&:last-child": { pb: 1.5 },
									}}
								>
									<Box display="flex" flexDirection="column" gap={0.25}>
										<Typography variant="body2">{files.image?.name}</Typography>
										<Typography variant="caption" color="text.secondary">
											{files.image?.size ? `${(files.image.size / 1024).toFixed(1)} KB` : ""}
										</Typography>
									</Box>
									<IconButton size="small" onClick={() => setFiles({ image: null })}>
										<Delete fontSize="small" />
									</IconButton>
								</CardContent>
							</Card>
						)}
					</Box>
				}
				actions={
					<>
						<Button onClick={() => setDialogOpen(false)} disabled={isSubmitting}>{t('BUTTON.CANCEL')}</Button>
						<Button variant="contained" onClick={handleSave} disabled={isSubmitting}>
							{isSubmitting ? (
								<>
									<CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
									{t('BUTTON.SUBMITTING')}
								</>
							) : (
								t('BUTTON.SAVE')
							)}
						</Button>
					</>
				}
			/>
		</div>
	);
};

export default ManageProductsMaster;